/**
 * 组织树。平台看全量，代理只看自己的子树 —— 同一段代码，范围由后端 `path LIKE` 收敛。
 *
 * 这里能做两件写操作：
 * - **创建下级**（可同时开一个 owner 账号）
 * - **停用 / 恢复 / 关闭**（只有上级能改状态，自己停自己没有意义）
 *
 * 后端不提供「改父节点」的接口：那要重写整棵子树的 path，
 * 跑一半失败就会造成权限错乱，必须走人工审批的运维脚本。所以这里也没有这个按钮。
 *
 * ## 层级怎么显示
 *
 * **不用缩进。** 列表是按 id 倒序排的（游标分页要求字典序即时间序），
 * 父行和子行根本不相邻 —— 在这种排序下画缩进纯属误导，看起来像树，实际不是。
 *
 * 改成两件东西：
 * - **上级列**：把父节点渲染成可点的标签，点一下就筛出它的全部下级。
 *   这样「谁是谁的上级」是读出来的，不是从缩进猜出来的。
 * - **归属链**：从物化路径还原出「平台 › 代理 › 商户」，让人一眼看到这个节点在树里的位置。
 *
 * 两者的数据都来自 `organizations.path`，也就是权限模型本身。
 */

import { Fragment, useState } from 'react';
import { request } from '../lib/api.ts';
import { usePagedList, useAsync, useMutation } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import {
  Badge, Button, Card, CopyId, ErrorBox, Field, Modal, Select, StatusBadge, TextInput,
} from '../components/ui.tsx';
import { DataTable, Pager, type Column } from '../components/table.tsx';
import { ORG_TYPE_LABEL, formatDateTime, pathSegments, shortId } from '../lib/format.ts';
import type { Org, OrgType } from '../lib/types.ts';

export function OrgsPage() {
  const session = useSession();
  const [type, setType] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  /** 收窄到某个节点的直接下级。由「上级」标签点击设置。 */
  const [parentId, setParentId] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Org | null>(null);

  const list = usePagedList<Org>('/v1/orgs', {
    type: type === '' ? null : type,
    status: status === '' ? null : status,
    q: q === '' ? null : q,
    parent_id: parentId === '' ? null : parentId,
  });

  /**
   * 名字查找表。
   *
   * 列表接口只回 `parent_id` 和 `path`，不回祖先的名字；而分页之后，一个节点的父节点
   * 很可能压根不在当前页里。所以单独拉一份全量（子树内，上限 200）来解析 id → 名字。
   * 解析不出来时退化成短 id，绝不显示空白 —— 空白会让人以为「没有上级」。
   */
  const lookup = useAsync<{ data: readonly Org[] }>('/v1/orgs', { query: { limit: 200 } });
  const nameOf = (id: string): string | null =>
    lookup.data?.data.find((org) => org.id === id)?.name ?? null;
  const typeOf = (id: string): OrgType | null =>
    lookup.data?.data.find((org) => org.id === id)?.type ?? null;

  const canCreate = session.can('admin') && session.me.org.status === 'active';

  const columns: readonly Column<Org>[] = [
    { key: 'name', header: '名称', render: (row) => (
      <div className="stack tight">
        <span className="cell-strong">{row.name}</span>
        <CopyId id={row.id} />
      </div>
    ) },
    { key: 'type', header: '类型', render: (row) => (
      <Badge tone={row.type === 'platform' ? 'info' : row.type === 'agent' ? 'warn' : 'neutral'}>
        {ORG_TYPE_LABEL[row.type]}
      </Badge>
    ) },
    /*
     * 上级。
     *
     * **平台不算上级。** 所有组织都挂在平台下，把「上级：平台」写在每一行代理上
     * 是一列恒定值 —— 占着位置却不携带任何信息。只有出现多级代理（代理挂代理）时，
     * 这一列才真正回答了「谁是谁的上级」。商户则一律有意义：它挂在哪个代理下决定了分润链路。
     */
    { key: 'parent', header: '上级', render: (row) => {
      if (row.parent_id === null || typeOf(row.parent_id) === 'platform') {
        return <span className="cell-muted">—</span>;
      }
      const parentName = nameOf(row.parent_id);
      const parentType = typeOf(row.parent_id);
      return (
        <button
          className="badge badge-link"
          title={`只看 ${parentName ?? row.parent_id} 的下级`}
          onClick={() => { setParentId(row.parent_id ?? ''); }}
        >
          {parentType !== null && `${ORG_TYPE_LABEL[parentType]} · `}
          {parentName ?? shortId(row.parent_id)}
        </button>
      );
    } },
    // 归属链：同样略去平台那一节，只画代理及以下
    { key: 'lineage', header: '归属', render: (row) => (
      <Lineage path={row.path} selfId={row.id} nameOf={nameOf} />
    ) },
    { key: 'status', header: '状态', render: (row) => <StatusBadge status={row.status} /> },
    { key: 'created', header: '创建时间', render: (row) => (
      <span className="cell-muted mono">{formatDateTime(row.created_at)}</span>
    ) },
    { key: 'actions', header: '', render: (row) => (
      // 只有严格上级能改状态，自己那行不给按钮
      row.id === session.me.org.id || !session.can('admin') ? null : (
        <Button size="sm" onClick={() => { setEditing(row); }}>管理</Button>
      )
    ) },
  ];

  return (
    <>
      <div className="row">
        <h1>组织</h1>
        <span style={{ flex: 1 }} />
        {canCreate && (
          <Button variant="primary" onClick={() => { setCreating(true); }}>创建下级</Button>
        )}
      </div>

      <div className="alert" data-tone="info">
        <span className="alert-title">平台 → 代理 → 商户，三层</span>
        <span>
          你能看到的只有<strong>自己和自己的下级</strong>，这由 <span className="mono">organizations.path</span> 决定
          （<span className="mono">{session.me.org.path}</span>）。
          点「上级」列的标签可以只看某个节点的直接下级；代理还能再往下发展商户，商户是叶子节点。
        </span>
      </div>

      <div className="filters">
        <Field label="类型">
          <Select
            value={type}
            onChange={setType}
            placeholder="全部"
            options={[
              { value: 'agent', label: '代理' },
              { value: 'merchant', label: '商户' },
            ]}
          />
        </Field>
        <Field label="状态">
          <Select
            value={status}
            onChange={setStatus}
            placeholder="全部"
            options={[
              { value: 'active', label: '启用' },
              { value: 'suspended', label: '暂停' },
              { value: 'closed', label: '已关闭' },
            ]}
          />
        </Field>
        <Field label="名称搜索">
          <TextInput value={q} onChange={setQ} placeholder="模糊匹配" />
        </Field>
      </div>

      {parentId !== '' && (
        <div className="row">
          <span className="card-sub">正在只看下级：</span>
          <Badge tone="info">{nameOf(parentId) ?? shortId(parentId)}</Badge>
          <Button size="sm" variant="ghost" onClick={() => { setParentId(''); }}>显示全部</Button>
        </div>
      )}

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.items}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          empty={parentId === '' ? '还没有下级组织' : '该节点下还没有组织'}
          emptyHint="创建代理可以让它再往下发展商户；创建商户则可以直接给它下发 MID。"
        />
        <Pager
          page={list.page}
          hasNext={list.hasNext}
          hasPrev={list.hasPrev}
          onNext={list.next}
          onPrev={list.prev}
          loading={list.loading}
          count={list.items.length}
        />
      </Card>

      {creating && (
        <CreateOrgModal
          onClose={() => { setCreating(false); }}
          onDone={() => { setCreating(false); list.reload(); lookup.reload(); }}
        />
      )}
      {editing !== null && (
        <ManageOrgModal
          org={editing}
          onClose={() => { setEditing(null); }}
          onDone={() => { setEditing(null); list.reload(); lookup.reload(); }}
        />
      )}
    </>
  );
}

/**
 * 归属链：`/平台/代理/商户/` → `代理 › 商户`，末节点加重。
 *
 * **平台那一节被略去。** 它是所有路径共有的前缀，画出来只是把每一行都加宽一截。
 * 于是「直接挂在平台下的代理」这一列显示为空 —— 正确，它本来就没有中间层。
 *
 * 中间的代理节点则**绝不省略**：少一节会让人以为这个商户直连平台，
 * 而实际上中间那个代理正在分润。
 *
 * 名字靠 `nameOf` 解析，解析不出来（超出可视范围、或超过 200 条上限）就退化成短 id。
 */
function Lineage({
  path, selfId, nameOf,
}: {
  path: string;
  selfId: string;
  nameOf: (id: string) => string | null;
}) {
  // 首段恒为平台根，跳过
  const segments = pathSegments(path).slice(1);
  if (segments.length <= 1) {
    return <span className="cell-muted">—</span>;
  }
  return (
    <span className="lineage">
      {segments.map((segment, index) => (
        <Fragment key={segment}>
          {index > 0 && <span className="lineage-sep">›</span>}
          <span
            className={segment === selfId ? 'lineage-self' : undefined}
            title={segment}
          >
            {nameOf(segment) ?? shortId(segment, 4)}
          </span>
        </Fragment>
      ))}
    </span>
  );
}

function CreateOrgModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const session = useSession();
  const [type, setType] = useState<OrgType>('merchant');
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [mcc, setMcc] = useState('');
  const [withOwner, setWithOwner] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const create = useMutation(async () => await request<Org>('/v1/orgs', {
    method: 'POST',
    body: {
      type,
      name,
      ...(country === '' ? {} : { country: country.toUpperCase() }),
      ...(mcc === '' ? {} : { mcc }),
      ...(withOwner ? { owner: { email, password } } : {}),
    },
  }));

  // 商户是叶子节点，不能再有下级 —— 代理只能建代理或商户，商户什么都建不了
  const allowedTypes = session.isPlatform || session.isAgent
    ? [{ value: 'merchant', label: '商户' }, { value: 'agent', label: '代理' }]
    : [];

  return (
    <Modal
      title="创建下级组织"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={create.pending || name.trim() === '' || (withOwner && (email === '' || password.length < 12))}
            onClick={() => { void create.run().then((result) => { if (result !== null) onDone(); }); }}
          >
            {create.pending ? '创建中…' : '创建'}
          </Button>
        </>
      }
    >
      <ErrorBox error={create.error} />

      <Field label="类型" hint="代理还能再往下发展商户；商户是叶子节点。">
        <Select value={type} onChange={(value) => { setType(value as OrgType); }} options={allowedTypes} />
      </Field>
      <Field label="名称">
        <TextInput value={name} onChange={setName} placeholder="例如：北极星科技" />
      </Field>
      <div className="grid cols-2">
        <Field label="国家" hint="ISO 3166-1 两位码，可留空">
          <TextInput value={country} onChange={setCountry} placeholder="US" />
        </Field>
        <Field label="MCC" hint="四位商户类别码，可留空">
          <TextInput value={mcc} onChange={setMcc} placeholder="5734" />
        </Field>
      </div>

      <label className="checkbox">
        <input type="checkbox" checked={withOwner} onChange={(e) => { setWithOwner(e.target.checked); }} />
        同时创建该组织的 owner 账号
      </label>

      {withOwner && (
        <>
          <Field label="Owner 邮箱" hint="该邮箱必须尚未注册；已注册的用户请用「成员」页面加进来。">
            <TextInput value={email} onChange={setEmail} type="email" />
          </Field>
          <Field label="Owner 初始口令" hint="至少 12 位。创建后请让对方立即修改。">
            <TextInput value={password} onChange={setPassword} type="password" />
          </Field>
        </>
      )}
    </Modal>
  );
}

function ManageOrgModal({
  org, onClose, onDone,
}: {
  org: Org;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(org.name);
  const [status, setStatus] = useState(org.status);

  const save = useMutation(async () => await request<Org>(`/v1/orgs/${org.id}`, {
    method: 'PATCH',
    body: {
      ...(name !== org.name ? { name } : {}),
      ...(status !== org.status ? { status } : {}),
    },
  }));

  const dirty = name !== org.name || status !== org.status;

  return (
    <Modal
      title={`管理 ${org.name}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant={status === 'closed' ? 'danger' : 'primary'}
            disabled={save.pending || !dirty}
            onClick={() => { void save.run().then((result) => { if (result !== null) onDone(); }); }}
          >
            {save.pending ? '保存中…' : '保存'}
          </Button>
        </>
      }
    >
      <ErrorBox error={save.error} />

      <Field label="名称">
        <TextInput value={name} onChange={setName} />
      </Field>

      <Field
        label="状态"
        hint="暂停会让该组织无法发起新交易，历史数据保留。关闭是终态，且要求它没有未关闭的下级、没有余额、没有在途提现。"
      >
        <Select
          value={status}
          onChange={(value) => { setStatus(value as Org['status']); }}
          options={[
            { value: 'active', label: '启用' },
            { value: 'suspended', label: '暂停' },
            { value: 'closed', label: '关闭（不可逆）' },
          ]}
        />
      </Field>

      {status === 'closed' && org.status !== 'closed' && (
        <div className="alert" data-tone="warn">
          <span className="alert-title">关闭是终态</span>
          <span>关闭前系统会检查：没有未关闭的下级、账户余额为 0、没有在途提现单。任何一项不满足都会被拒绝。</span>
        </div>
      )}
    </Modal>
  );
}
